import { type FC, useState } from "react";

import { BasicModal } from "@similarweb/ui-components";

import { ExitConfirm } from "app/components/modals/components/ExitConfirm";
import { useOnEscape } from "app/custom-hooks/useOnKeyUp";
import type { ICountryObject } from "app/services/CountryService";
import { type IUserKeywordGroup, useKeywordGroups } from "app/services/keyword-groups";

import { KeywordGroupNameProvider } from "./KeywordGroupNameContext";
import { KeywordListComposerProvider, useKeywordListComposerContext } from "./KeywordListComposerContext";
import { KeywordListComposerCreator } from "./KeywordListComposerCreator";
import { KeywordListComposerEditor } from "./KeywordListComposerEditor";
import { KeywordListComposerPreview } from "./KeywordListComposerPreview";
import { trackPopupClick } from "./tracking";
import type { Mode } from "./types";

const ComponentsRegistry: Record<Mode, FC<{ onCloseConfirm: VoidFunction }>> = {
    init_with_seed: KeywordListComposerPreview,
    empty_list: KeywordListComposerCreator,
    edit_existing_list: KeywordListComposerEditor,
};

interface KeywordListComposerModalProps {
    onClose: VoidFunction;
}

const KeywordListComposerModal: FC<KeywordListComposerModalProps> = ({ onClose }) => {
    const { mode, shouldConfirmBeforeClose } = useKeywordListComposerContext();
    const [showConfirm, setShowConfirm] = useState(false);
    const KeywordListComposerContent = ComponentsRegistry[mode];

    const handleClose = () => {
        setShowConfirm(false);
        onClose();
    };

    const handleCloseWithConfirm = () => {
        trackPopupClick({ event: "Cancel" });
        if (shouldConfirmBeforeClose) {
            setShowConfirm(true);
        } else {
            handleClose();
        }
    };

    useOnEscape(handleCloseWithConfirm);

    return (
        <BasicModal
            isOpen
            customStyles={{ content: { width: "unset", padding: 0 }, overlay: { zIndex: 1021 } }}
            closeIconConfig={{ show: false, onClick: handleClose }}
            onRequestClose={handleCloseWithConfirm}
        >
            {showConfirm ? (
                <ExitConfirm
                    onClose={handleClose}
                    displayMainModal={() => setShowConfirm(false)}
                />
            ) : (
                <KeywordListComposerContent onCloseConfirm={handleCloseWithConfirm} />
            )}
        </BasicModal>
    );
};

interface KeywordListComposerProps extends KeywordListComposerModalProps {
    seedKeyword?: string;
    keywordGroupToEdit?: IUserKeywordGroup;
    country?: ICountryObject;
}

export const KeywordListComposer = ({
    seedKeyword,
    keywordGroupToEdit,
    country,
    onClose,
}: KeywordListComposerProps) => {
    const { data: userGroups } = useKeywordGroups();

    return (
        <KeywordListComposerProvider
            seedKeyword={seedKeyword}
            keywordGroupToEdit={keywordGroupToEdit}
            country={country}
        >
            <KeywordGroupNameProvider userGroups={userGroups}>
                <KeywordListComposerModal onClose={onClose} />
            </KeywordGroupNameProvider>
        </KeywordListComposerProvider>
    );
};
